/* =============================================================================
   Arman Curren — theme JS
   Owns: window.AC cart API, cart drawer, countdowns, sticky header, mobile nav,
   reservation timer, cart page section refresh.

   window.AC.config is printed by sections/site-header.liquid before this file.
   Public API (see BRIEF):
     AC.addToCart([{id, quantity}], {open:true}) -> Promise<cart>
     AC.changeLine(lineKeyOrId, quantity)        -> Promise<cart>
     AC.getCart() / AC.refresh()                 -> Promise<cart>
     AC.openDrawer() / AC.closeDrawer()
     AC.formatMoney(cents)
     events: ac:cart-open (open drawer), ac:cart-updated (detail = cart)
     click-to-add: [data-ac-add="<variantId>"] (+ optional data-ac-qty)
     badges: [data-ac-cart-count]

   The drawer markup below mirrors snippets/cart-drawer.liquid, which in turn
   mirrors the boutique app/components/cart-drawer.tsx class for class.
   ========================================================================== */
(function () {
  "use strict";

  var AC = (window.AC = window.AC || {});

  var RESERVE_MS = 10 * 60 * 1000;
  var RESERVE_KEY = "ac-cart-reserved-at";
  var TIERS = [
    { n: 1, off: 0 },
    { n: 2, off: 15 },
    { n: 3, off: 20 },
    { n: 4, off: 30 }
  ];

  var state = {
    cart: null,
    prevCount: null,
    reservedAt: 0,
    drawerOpen: false,
    drawerState: "",
    removed: null,
    recsPool: null,
    compareMap: null,
    undoTimeout: null,
    lastFocus: null
  };

  /* ---------------------------------------------------------------- helpers */

  function config() {
    return AC.config || {};
  }

  function route(key, fallback) {
    var routes = config().routes || {};
    return routes[key] || fallback;
  }

  // Shopify route helpers have no ".js" suffix (routes.cart_add_url === "/cart/add"),
  // but the AJAX API lives at "/cart/add.js".
  function endpoint(key, fallback) {
    var url = route(key, fallback);
    return url.indexOf(".js") === -1 ? url + ".js" : url;
  }

  var numberFormats = {};

  function money(cents, decimals) {
    var value = Number(cents || 0) / 100;
    var currency = config().currency || "USD";
    var min = decimals === 0 ? 0 : 2;
    var key = currency + "-" + min;
    try {
      if (!numberFormats[key]) {
        numberFormats[key] = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: currency,
          minimumFractionDigits: min,
          maximumFractionDigits: Math.max(min, 2)
        });
      }
      return numberFormats[key].format(value);
    } catch (error) {
      return "$" + value.toFixed(min);
    }
  }

  // Product prices are whole-dollar strings in the boutique ("$159").
  function moneyShort(cents) {
    return money(cents, 0);
  }

  function esc(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function pad(n) {
    return String(Math.max(0, Math.floor(n))).padStart(2, "0");
  }

  function watches(n) {
    return n + (n === 1 ? " watch" : " watches");
  }

  function imageUrl(url, width) {
    if (!url) return "";
    return url + (url.indexOf("?") > -1 ? "&" : "?") + "width=" + width;
  }

  function parseEnds(value) {
    if (!value) return 0;
    var time = new Date(value).getTime();
    return isNaN(time) ? 0 : time;
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function emit(name, detail) {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (error) {
      var event = document.createEvent("CustomEvent");
      event.initCustomEvent(name, true, true, detail);
      window.dispatchEvent(event);
    }
  }

  function closingDate() {
    var ends = parseEnds(config().saleEndsAt);
    if (!ends) return "";
    return new Date(ends).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
  }

  /* ------------------------------------------------------------------ fetch */

  function request(url, options) {
    options = options || {};
    options.credentials = "same-origin";
    options.headers = Object.assign(
      { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      options.headers || {}
    );
    return fetch(url, options).then(function (response) {
      return response.text().then(function (text) {
        var body = null;
        try {
          body = text ? JSON.parse(text) : null;
        } catch (error) {
          body = null;
        }
        if (!response.ok) {
          var message =
            (body && (body.description || body.message)) ||
            "Sorry, something went wrong. Please try again.";
          var failure = new Error(message);
          failure.status = response.status;
          throw failure;
        }
        return body;
      });
    });
  }

  function postJSON(url, payload) {
    return request(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
  }

  function getCart() {
    return request(endpoint("cart", "/cart.js")).then(function (cart) {
      state.cart = cart;
      return cart;
    });
  }

  function refresh() {
    return getCart().then(function (cart) {
      render();
      return cart;
    });
  }

  function addToCart(items, options) {
    options = options || {};
    var list = (Array.isArray(items) ? items : [items])
      .map(function (item) {
        return {
          id: Number(item.id || item.variant_id || item.variantId),
          quantity: Math.max(1, Number(item.quantity || item.qty || 1))
        };
      })
      .filter(function (item) {
        return !!item.id;
      });

    if (!list.length) return Promise.reject(new Error("Missing variant id"));

    return postJSON(endpoint("cartAdd", "/cart/add.js"), { items: list })
      .then(function () {
        return refresh();
      })
      .then(function (cart) {
        emit("ac:cart-updated", cart);
        if (options.open !== false) openDrawer();
        return cart;
      })
      .catch(function (error) {
        emit("ac:cart-error", error);
        throw error;
      });
  }

  function changeLine(id, quantity) {
    var payload;
    if (id && typeof id === "object") {
      payload = {
        id: id.id || id.key || id.line,
        quantity: Number(id.quantity || 0)
      };
    } else {
      payload = { id: id, quantity: Number(quantity || 0) };
    }
    if (payload.id == null || payload.id === "") {
      return Promise.reject(new Error("Missing line id"));
    }
    return postJSON(endpoint("cartChange", "/cart/change.js"), payload).then(function (cart) {
      state.cart = cart;
      render();
      emit("ac:cart-updated", cart);
      return cart;
    });
  }

  function updateCart(payload) {
    return postJSON(endpoint("cartUpdate", "/cart/update.js"), payload).then(function (cart) {
      state.cart = cart;
      render();
      emit("ac:cart-updated", cart);
      return cart;
    });
  }

  /* -------------------------------------------------------------- rendering */

  function render() {
    var cart = state.cart;
    if (!cart) return;
    trackReservation(cart);
    renderCounts(cart);
    renderDrawer(cart);
    tick();
  }

  function renderCounts(cart) {
    document.querySelectorAll("[data-ac-cart-count]").forEach(function (el) {
      var next = String(cart.item_count);
      if (el.textContent !== next) {
        el.textContent = next;
        pop(el);
      }
    });
    document.querySelectorAll("[data-ac-cart-open]").forEach(function (el) {
      el.setAttribute("aria-label", "Cart, " + cart.item_count + " items");
    });
  }

  function pop(el) {
    if (reducedMotion() || !el.animate) return;
    el.animate(
      [{ transform: "scale(1)" }, { transform: "scale(1.6)" }, { transform: "scale(1)" }],
      { duration: 450, easing: "ease-out" }
    );
  }

  /* ---------------------------------------------------------------- drawer */

  function drawerRoot() {
    return document.querySelector("[data-ac-drawer]");
  }

  function recsData(root) {
    if (state.recsPool) return state.recsPool;
    var node = root.querySelector("[data-ac-recs-data]");
    if (!node) return [];
    try {
      var list = JSON.parse(node.textContent);
      state.recsPool = Array.isArray(list) ? list : [];
    } catch (error) {
      state.recsPool = [];
    }
    return state.recsPool;
  }

  function compareMap(root) {
    if (state.compareMap) return state.compareMap;
    var node = root.querySelector("[data-ac-compare-data]");
    if (!node) return {};
    try {
      state.compareMap = JSON.parse(node.textContent) || {};
    } catch (error) {
      state.compareMap = {};
    }
    return state.compareMap;
  }

  function lineCompare(item, compares) {
    var map = compares || state.compareMap || {};
    var value = Number(map[item.handle] || 0);
    if (!value) value = Number(item.compare_at_price || 0);
    return value > 0 ? value : 0;
  }

  function model(cart, root) {
    var compares = compareMap(root);
    var lines = cart.items || [];
    var items = lines;

    var count = items.reduce(function (n, item) {
      return n + item.quantity;
    }, 0);

    var subtotal = items.reduce(function (n, item) {
      return n + (item.final_price != null ? item.final_price : item.price) * item.quantity;
    }, 0);
    var retail = items.reduce(function (n, item) {
      var was = lineCompare(item, compares) || (item.final_price != null ? item.final_price : item.price);
      return n + was * item.quantity;
    }, 0);

    var tier = null;
    TIERS.forEach(function (step) {
      if (count >= step.n) tier = step;
    });
    var next = null;
    for (var i = 0; i < TIERS.length; i += 1) {
      if (count < TIERS[i].n) {
        next = TIERS[i];
        break;
      }
    }
    var off = tier ? tier.off : 0;
    var discount = Math.round((subtotal * off) / 100);
    var total = subtotal - discount;
    var saved = Math.max(0, retail - total);
    var savedPct = retail > 0 ? Math.round((saved / retail) * 100) : 0;
    var rate = (next || tier || { off: 0 }).off;

    var inBag = {};
    lines.forEach(function (item) {
      inBag[item.handle] = true;
    });
    var recs = recsData(root)
      .filter(function (product) {
        return !inBag[product.handle];
      })
      .slice(0, 8);

    return {
      items: items,
      count: count,
      subtotal: subtotal,
      retail: retail,
      tier: tier,
      next: next,
      off: off,
      discount: discount,
      total: total,
      saved: saved,
      savedPct: savedPct,
      rate: rate,
      recs: recs,
      bagCount: lines.reduce(function (n, item) {
        return n + item.quantity;
      }, 0)
    };
  }

  var Icons = {
    trash:
      '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"/></svg>',
    lock:
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="1.5"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
    arrow:
      '<span class="transition-transform group-hover:translate-x-1"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>',
    clock:
      '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2.5M9 2h6"/></svg>',
    truck:
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M2 6h11v10H2zM13 9h4l3 3v4h-7"/><circle cx="6" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>',
    undo:
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M9 14 4 9l5-5"/><path d="M4 9h10a6 6 0 0 1 0 12h-3"/></svg>',
    shield:
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M12 3 4 6v6c0 4.5 3.4 8 8 9 4.6-1 8-4.5 8-9V6l-8-3Z"/><path d="m8.5 12 2.5 2.5 4.5-5"/></svg>'
  };

  function qtyControls(key, qty) {
    return (
      '<div class="flex h-9 items-center rounded-full border border-line">' +
      '<button type="button" aria-label="Decrease quantity" class="grid h-full w-9 place-items-center text-lg" data-ac-line-qty="' + esc(key) + '" data-delta="-1">&minus;</button>' +
      '<span class="w-6 text-center text-sm font-semibold tabular-nums" aria-live="polite">' + qty + "</span>" +
      '<button type="button" aria-label="Increase quantity" class="grid h-full w-9 place-items-center text-lg" data-ac-line-qty="' + esc(key) + '" data-delta="1">+</button>' +
      "</div>"
    );
  }

  function lineHTML(item) {
    var price = item.final_price != null ? item.final_price : item.price;
    var compare = lineCompare(item);
    var pct = compare > price ? Math.round((1 - price / compare) * 100) : 0;
    var save = Math.max(0, (compare > 0 ? compare : price) - price) * item.quantity;
    var url = item.url || "/products/" + item.handle;

    return (
      '<li class="flex gap-3 bg-white p-3" data-ac-line="' + esc(item.key) + '">' +
        '<a href="' + esc(url) + '" class="relative size-20 shrink-0 overflow-hidden bg-white sm:size-[92px]">' +
          (item.image
            ? '<img src="' + esc(imageUrl(item.image, 200)) + '" alt="' + esc(item.product_title) + '" width="200" height="200" loading="lazy" class="object-contain" style="position:absolute;inset:0;width:100%;height:100%">'
            : "") +
          (pct
            ? '<span class="absolute left-0 top-0 bg-emerald px-1.5 py-0.5 text-[10px] font-bold text-white">&minus;' + pct + "%</span>"
            : "") +
        "</a>" +
        '<div class="flex min-w-0 flex-1 flex-col">' +
          '<div class="flex items-start gap-2">' +
            '<a href="' + esc(url) + '" class="line-clamp-2 flex-1 text-[14px] font-semibold leading-tight">' + esc(item.product_title) + "</a>" +
            '<button type="button" aria-label="Remove ' + esc(item.product_title) + '" class="-mr-1.5 -mt-1.5 grid size-9 shrink-0 place-items-center text-muted transition-colors hover:text-ink" data-ac-line-remove="' + esc(item.key) + '">' + Icons.trash + "</button>" +
          "</div>" +
          '<span class="mt-1 flex flex-wrap items-baseline gap-x-1.5">' +
            '<b class="text-[15px] text-emerald-deep">' + moneyShort(price) + '<small class="ml-1 text-[10px] font-semibold tracking-[.1em]">USD</small></b>' +
            (compare > price ? '<s class="text-xs text-[#8a8a8a]">' + moneyShort(compare) + "</s>" : "") +
          "</span>" +
          '<div class="mt-auto flex flex-wrap items-end justify-between gap-x-2 gap-y-1 pt-2">' +
            qtyControls(item.key, item.quantity) +
            (save > 0
              ? '<span class="text-[11px] font-semibold leading-tight text-emerald-deep min-[380px]:text-right">You save<br class="max-[379px]:hidden"> <b class="text-[13px]">' + money(save) + "</b></span>"
              : "") +
          "</div>" +
        "</div>" +
      "</li>"
    );
  }

  function ladderHTML(m) {
    var progress = ((Math.max(1, Math.min(4, m.count)) - 1) / 3) * 75;
    var steps = TIERS.map(function (step) {
      var done = m.count >= step.n;
      var current = m.tier && m.tier.off === step.off ? true : !m.tier && step.n === 1;
      return (
        '<li class="relative flex flex-col items-center text-center"' + (current ? ' aria-current="step"' : "") + ">" +
          '<span class="grid size-[22px] place-items-center rounded-full border-2 text-[10px] font-bold transition-colors ' +
            (done ? "border-emerald bg-emerald text-white" : "border-line bg-white text-muted") + '">' +
            (done ? "&#10003;" : step.n) +
          "</span>" +
          '<b class="mt-1 text-[11px] leading-tight ' + (done ? "text-emerald-deep" : "text-ink") + '">' +
            (step.off ? "&minus;" + step.off + "%" : "Sale") +
          "</b>" +
          '<small class="text-[10px] leading-none text-muted">' + step.n + (step.n === 4 ? "+" : "") + " " + (step.n === 1 ? "watch" : "watches") + "</small>" +
        "</li>"
      );
    }).join("");

    var text = m.next
      ? "Add <b>" + (m.next.n - m.count) + '</b> more for <b class="text-emerald-deep">extra ' + m.next.off + "% off</b> your whole order"
      : 'Best deal unlocked: <b class="text-emerald-deep">extra ' + m.off + "% off</b> everything";

    return (
      '<section class="border-b border-line bg-white px-4 pb-3 pt-3 sm:px-5" aria-label="Bundle discount">' +
        '<p class="mb-2.5 text-[13px] leading-snug">' + text + "</p>" +
        '<ol class="relative grid grid-cols-4" aria-label="Bundle discount progress">' +
          '<span class="absolute left-[12.5%] right-[12.5%] top-[10px] h-0.5 bg-line" aria-hidden="true"></span>' +
          '<span class="absolute left-[12.5%] top-[10px] h-0.5 bg-emerald transition-[width] duration-500 ease-out" style="width:' + progress + '%" aria-hidden="true"></span>' +
          steps +
        "</ol>" +
      "</section>"
    );
  }

  function recCardHTML(product, rate) {
    var now = rate > 0 ? Math.round(product.price * (1 - rate / 100)) : product.price;
    var was = rate > 0 ? product.price : product.compare;
    return (
      '<div class="flex w-[136px] shrink-0 snap-start flex-col bg-white p-2.5 sm:w-[148px]">' +
        '<a href="' + esc(product.url) + '" class="relative block aspect-square bg-white">' +
          (product.image
            ? '<img src="' + esc(product.image) + '" alt="' + esc(product.title) + '" width="300" height="300" loading="lazy" class="object-contain" style="position:absolute;inset:0;width:100%;height:100%">'
            : "") +
          (product.best
            ? '<span class="absolute left-0 top-0 bg-ink px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[.1em] text-white">Best seller</span>'
            : "") +
        "</a>" +
        '<a href="' + esc(product.url) + '" class="mt-1 line-clamp-2 min-h-8 text-[12px] font-semibold leading-tight">' + esc(product.title) + "</a>" +
        '<span class="mt-1 flex flex-wrap items-baseline gap-x-1.5">' +
          '<b class="text-[14px] text-emerald-deep">' + moneyShort(now) + "</b>" +
          (was > 0 ? '<s class="text-[11px] text-[#8a8a8a]">' + moneyShort(was) + "</s>" : "") +
        "</span>" +
        (rate > 0 ? '<small class="text-[10px] text-muted">with &minus;' + rate + "% bundle</small>" : "") +
        '<button type="button" class="mt-2 h-9 w-full bg-ink text-[10px] font-bold uppercase tracking-[.14em] text-white transition-colors hover:bg-emerald-dark active:scale-[.97]" data-ac-add="' + product.variant + '" data-ac-qty="1">+ Add</button>' +
      "</div>"
    );
  }

  function recsHTML(m) {
    if (!m.recs.length) return "";
    var title = m.next
      ? "Add " + (m.next.n - m.count === 1 ? "one" : m.next.n - m.count) + ' &middot; unlock <span class="text-emerald-deep">&minus;' + m.next.off + "%</span>"
      : "Complete the collection";
    return (
      '<section class="py-4" aria-label="Add to your bundle">' +
        '<div class="mb-3 flex items-baseline justify-between px-4">' +
          '<h3 class="text-[11px] font-bold uppercase tracking-[.16em]">' + title + "</h3>" +
          '<span class="text-[11px] text-muted max-[379px]:hidden">Best sellers</span>' +
        "</div>" +
        '<div class="no-scrollbar flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-px-4 px-4">' +
          m.recs.map(function (product) {
            return recCardHTML(product, m.rate);
          }).join("") +
        "</div>" +
      "</section>"
    );
  }

  function trustHTML() {
    return (
      '<ul class="grid grid-cols-3 gap-2 px-3 py-4 text-center text-[11px] leading-tight sm:px-4">' +
        '<li class="flex flex-col items-center gap-1.5 bg-white px-2 py-3">' + Icons.truck + "<span><b>Free shipping</b><br><span class=\"text-muted\">Tracked, worldwide</span></span></li>" +
        '<li class="flex flex-col items-center gap-1.5 bg-white px-2 py-3">' + Icons.undo + "<span><b>30-day returns</b><br><span class=\"text-muted\">Easy, from delivery</span></span></li>" +
        '<li class="flex flex-col items-center gap-1.5 bg-white px-2 py-3">' + Icons.shield + "<span><b>Secure payment</b><br><span class=\"text-muted\">SSL encrypted</span></span></li>" +
      "</ul>"
    );
  }

  function footerHTML(m) {
    return (
      '<footer class="shrink-0 bg-white px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_24px_#0000000f] sm:px-5">' +
        '<dl class="mb-3 space-y-1 text-[13px]">' +
          '<div class="flex justify-between"><dt class="text-muted">Subtotal</dt><dd class="tabular-nums">' + money(m.subtotal) + ' <small class="text-[10px] font-semibold tracking-[.1em]">USD</small></dd></div>' +
          (m.discount
            ? '<div class="flex justify-between text-emerald-deep"><dt>Bundle discount <span class="text-[11px] font-normal">(' + m.off + '% off subtotal)</span></dt><dd class="tabular-nums">&minus;' + money(m.discount) + "</dd></div>"
            : "") +
          '<div class="flex justify-between border-t border-line pt-1 text-emerald-deep"><dt>You&#39;re saving <span class="text-[11px] font-normal">(vs. retail price)</span></dt><dd class="animate-[bump_.5s_ease-out] tabular-nums">' + money(m.saved) + " <span>(" + m.savedPct + "% off)</span></dd></div>" +
          '<div class="flex justify-between"><dt class="text-muted">Shipping</dt><dd class="font-semibold text-emerald-deep">Free</dd></div>' +
          '<div class="flex items-baseline justify-between border-t border-line pt-1.5 text-base font-bold"><dt>Total</dt><dd class="tabular-nums">' + money(m.total) + ' <small class="text-[10px] font-semibold tracking-[.1em]">USD</small></dd></div>' +
        "</dl>" +
        '<button type="button" class="group relative flex h-13 w-full items-center justify-center gap-2.5 overflow-hidden bg-emerald text-[13px] font-bold uppercase tracking-[.16em] text-white transition-colors hover:bg-emerald-dark active:scale-[.99]" data-ac-checkout>' +
          '<span class="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 animate-[sheen_3.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/25 to-transparent" aria-hidden="true"></span>' +
          Icons.lock + "Secure checkout" + Icons.arrow +
        "</button>" +
        '<p class="mt-1.5 text-center text-[10px] text-muted">Bundle discount applied automatically</p>' +
      "</footer>"
    );
  }

  function timerHTML(m) {
    return (
      '<div class="flex shrink-0 items-center justify-between gap-2 whitespace-nowrap bg-ink px-4 py-2 text-[10px] text-white/80 min-[380px]:text-[11px] sm:px-5" role="timer">' +
        '<span class="flex items-center gap-1.5">' + Icons.clock + 'Reserved for <b class="tabular-nums text-emerald-glow" data-ac-reserve>10:00</b></span>' +
        '<span class="tabular-nums" data-ac-sale-wrap><span class="max-[379px]:hidden">Sale </span>ends in <b class="text-white" data-ac-sale-short>0d 00h</b></span>' +
      "</div>"
    );
  }

  function panelHTML(cart, m) {
    var body;
    if (m.bagCount > 0) {
      body =
        timerHTML(m) +
        '<div class="flex-1 overflow-y-auto overscroll-contain">' +
          ladderHTML(m) +
          '<ul class="space-y-2 p-3 sm:px-4">' +
            m.items.map(lineHTML).join("") +
          "</ul>" +
          (state.removed
            ? '<div class="mx-3 flex items-center justify-between bg-ink px-4 py-2.5 text-[13px] text-white sm:mx-4" role="status" data-ac-undo-bar>' +
              '<span class="truncate">Removed from your bag</span>' +
              '<button type="button" class="ml-3 shrink-0 font-bold uppercase tracking-[.12em] text-emerald-glow" data-ac-undo>Undo</button>' +
            "</div>"
            : "") +
          recsHTML(m) +
          trustHTML() +
        "</div>" +
        footerHTML(m);
    } else {
      body =
        '<div class="flex-1 overflow-y-auto">' +
          '<div class="flex flex-col items-center px-8 pb-6 pt-10 text-center">' +
            '<span class="grid size-16 place-items-center rounded-full bg-white">' +
              '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 8h14l-1.2 12H6.2L5 8Z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg>' +
            "</span>" +
            '<p class="mt-4 font-serif text-2xl font-semibold">Your bag is empty</p>' +
            '<p class="mt-2 text-sm text-muted">Up to <b class="text-emerald-deep">' + (config().maxOff || 85) + '% off</b> every watch until ' + closingDate() + ' &mdash; plus up to <b class="text-ink">30% extra</b> when you buy more.</p>' +
            '<a href="/collections/best-sellers" class="mt-5 inline-flex h-12 items-center bg-emerald px-6 text-xs font-bold uppercase tracking-[.16em] text-white" data-ac-drawer-close>Shop best sellers</a>' +
          "</div>" +
          '<div class="grid grid-cols-2 gap-2.5 px-4 pb-6">' +
            recsData(drawerRoot()).slice(0, 4).map(function (product) {
              var pct = product.compare > product.price ? Math.round((1 - product.price / product.compare) * 100) : 0;
              return (
                '<div class="flex flex-col bg-white p-2.5">' +
                  '<a href="' + esc(product.url) + '" class="relative block aspect-square bg-white">' +
                    (product.image ? '<img src="' + esc(product.image) + '" alt="' + esc(product.title) + '" width="300" height="300" loading="lazy" class="object-contain" style="position:absolute;inset:0;width:100%;height:100%">' : "") +
                    (pct ? '<span class="absolute left-0 top-0 bg-emerald px-1.5 py-0.5 text-[10px] font-bold text-white">&minus;' + pct + "%</span>" : "") +
                  "</a>" +
                  '<a href="' + esc(product.url) + '" class="mt-1 line-clamp-2 min-h-8 text-[12px] font-semibold leading-tight">' + esc(product.title) + "</a>" +
                  '<span class="mt-1 flex flex-wrap items-baseline gap-x-1.5"><b class="text-[14px] text-emerald-deep">' + moneyShort(product.price) + "</b>" +
                    (product.compare > product.price ? '<s class="text-[11px] text-[#8a8a8a]">' + moneyShort(product.compare) + "</s>" : "") +
                  "</span>" +
                  '<button type="button" class="mt-2 h-9 w-full bg-ink text-[10px] font-bold uppercase tracking-[.14em] text-white" data-ac-add="' + product.variant + '" data-ac-qty="1">+ Add</button>' +
                "</div>"
              );
            }).join("") +
          "</div>" +
        "</div>";
    }

    return (
      '<div class="absolute inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity duration-300 ' +
        (state.drawerOpen ? "opacity-100" : "opacity-0") + '" data-ac-drawer-overlay></div>' +
      '<button type="button" tabindex="-1" aria-hidden="true" class="absolute left-[9vw] top-[calc(env(safe-area-inset-top)+8px)] grid size-10 -translate-x-1/2 place-items-center rounded-full bg-white text-xl text-ink shadow-lg transition-opacity duration-300 sm:hidden ' +
        (state.drawerOpen ? "opacity-100 delay-150" : "opacity-0") + '" data-ac-drawer-x>&times;</button>' +
      '<aside role="dialog" aria-modal="true" aria-label="Shopping bag" class="absolute inset-y-0 right-0 flex w-[82vw] flex-col bg-off text-ink shadow-[-12px_0_40px_#0004] transition-transform duration-300 ease-out sm:w-[440px] ' +
        (state.drawerOpen ? "translate-x-0" : "translate-x-full") + '" data-ac-drawer-panel data-state="' + (m.bagCount > 0 ? "filled" : "empty") + '">' +
        '<header class="flex h-14 shrink-0 items-center justify-between bg-white px-4 pt-[env(safe-area-inset-top)] sm:px-5">' +
          '<h2 class="flex items-baseline gap-2">' +
            '<span class="font-serif text-xl font-semibold">Your bag</span>' +
            (m.count ? '<span class="text-xs font-semibold text-muted">' + watches(m.count) + "</span>" : "") +
          "</h2>" +
          '<button type="button" aria-label="Close bag" class="-mr-2 grid size-11 place-items-center text-2xl" data-ac-drawer-close>&times;</button>' +
        "</header>" +
        body +
      "</aside>"
    );
  }

  function renderDrawer(cart) {
    var root = drawerRoot();
    if (!root) return;
    var m = model(cart, root);
    root.innerHTML = panelHTML(cart, m);
    state.drawerState = m.bagCount > 0 ? "filled" : "empty";
    tick();
  }

  /* ------------------------------------------------- reservation + countdown */

  function trackReservation(cart) {
    var count = cart.item_count;
    if (count > 0) {
      var stamp = 0;
      try {
        stamp = Number(window.localStorage.getItem(RESERVE_KEY)) || 0;
      } catch (error) {
        stamp = 0;
      }
      var now = Date.now();
      if (state.prevCount === 0) stamp = 0;
      if (!stamp || stamp > now || stamp + RESERVE_MS <= now) {
        stamp = now;
        try {
          window.localStorage.setItem(RESERVE_KEY, String(stamp));
        } catch (error) {
          /* private mode: keep it in memory only */
        }
      }
      state.reservedAt = stamp;
    } else {
      state.reservedAt = 0;
    }
    state.prevCount = count;
  }

  function setCountdown(el, values) {
    for (var i = 0; i < values.length; i += 1) {
      var cell = el.querySelector('[data-ac-cd="' + i + '"]');
      if (cell) cell.textContent = pad(values[i]);
    }
  }

  function tick() {
    var now = Date.now();

    document.querySelectorAll("[data-ac-countdown]").forEach(function (el) {
      var ends = parseEnds(el.getAttribute("data-ends-at"));
      if (!ends) return;
      var left = Math.max(0, Math.floor((ends - now) / 1000));
      setCountdown(el, [
        Math.floor(left / 86400),
        Math.floor(left / 3600) % 24,
        Math.floor(left / 60) % 60,
        left % 60
      ]);
    });

    document.querySelectorAll("[data-ac-promo]").forEach(function (el) {
      var ends = parseEnds(el.getAttribute("data-ends-at"));
      if (!ends) return;
      var over = ends <= now;
      if (el.getAttribute("data-over") === String(over)) return;
      el.setAttribute("data-over", String(over));
      var kicker = el.querySelector("[data-ac-promo-kicker]");
      var headline = el.querySelector("[data-ac-promo-headline]");
      if (kicker) kicker.textContent = over ? el.getAttribute("data-kicker-over") : el.getAttribute("data-kicker-open");
      if (headline) headline.textContent = over ? el.getAttribute("data-headline-over") : el.getAttribute("data-headline-open");
      el.querySelectorAll("[data-ac-promo-extra]").forEach(function (node) {
        node.style.display = over ? "none" : "";
      });
      var cd = el.querySelector("[data-ac-countdown]");
      if (cd) cd.style.display = over ? "none" : "";
    });

    var ends = parseEnds(config().saleEndsAt);
    document.querySelectorAll("[data-ac-sale-short]").forEach(function (el) {
      if (!ends) return;
      var left = Math.max(0, Math.floor((ends - now) / 1000));
      el.textContent = Math.floor(left / 86400) + "d " + pad(Math.floor(left / 3600) % 24) + "h";
    });
    document.querySelectorAll("[data-ac-sale-wrap]").forEach(function (el) {
      if (!ends) return;
      el.style.display = ends > now ? "" : "none";
    });

    renderReservation(now);
  }

  function renderReservation(now) {
    var cart = state.cart;
    if (cart && cart.item_count > 0) {
      if (!state.reservedAt) trackReservation(cart);
      if (state.reservedAt && state.reservedAt + RESERVE_MS <= now) {
        state.reservedAt = now;
        try {
          window.localStorage.setItem(RESERVE_KEY, String(now));
        } catch (error) {
          /* ignore */
        }
      }
    }

    var left = state.reservedAt ? Math.max(0, state.reservedAt + RESERVE_MS - now) : 0;
    document.querySelectorAll("[data-ac-reserve]").forEach(function (el) {
      el.textContent = pad(Math.floor(left / 60000)) + ":" + pad(Math.floor(left / 1000) % 60);
    });
    document.querySelectorAll("[data-ac-reserve-wrap]").forEach(function (el) {
      el.style.display = left > 0 ? "" : "none";
    });
  }

  /* ----------------------------------------------------------------- burger */

  function openDrawer() {
    var root = drawerRoot();
    if (!root) return;

    state.lastFocus = document.activeElement;
    state.drawerOpen = true;
    root.classList.remove("pointer-events-none");
    root.setAttribute("aria-hidden", "false");
    root.removeAttribute("inert");

    var overlay = root.querySelector("[data-ac-drawer-overlay]");
    if (overlay) {
      overlay.classList.remove("opacity-0");
      overlay.classList.add("opacity-100");
    }
    var panel = root.querySelector("[data-ac-drawer-panel]");
    if (panel) {
      panel.classList.remove("translate-x-full");
      panel.classList.add("translate-x-0");
    }
    var mobileX = root.querySelector("[data-ac-drawer-x]");
    if (mobileX) {
      mobileX.classList.remove("opacity-0");
      mobileX.classList.add("opacity-100", "delay-150");
    }

    document.body.style.overflow = "hidden";

    var close = root.querySelector("[data-ac-drawer-close]");
    if (close) {
      try {
        close.focus({ preventScroll: true });
      } catch (error) {
        close.focus();
      }
    }

    if (!state.cart) {
      refresh().catch(function () {
        /* keep the server-rendered drawer if the request fails */
      });
    }
  }

  function closeDrawer() {
    var root = drawerRoot();
    if (!root) return;

    state.drawerOpen = false;
    root.classList.add("pointer-events-none");
    root.setAttribute("aria-hidden", "true");
    root.setAttribute("inert", "");

    var overlay = root.querySelector("[data-ac-drawer-overlay]");
    if (overlay) {
      overlay.classList.add("opacity-0");
      overlay.classList.remove("opacity-100");
    }
    var panel = root.querySelector("[data-ac-drawer-panel]");
    if (panel) {
      panel.classList.add("translate-x-full");
      panel.classList.remove("translate-x-0");
    }
    var mobileX = root.querySelector("[data-ac-drawer-x]");
    if (mobileX) {
      mobileX.classList.add("opacity-0");
      mobileX.classList.remove("opacity-100", "delay-150");
    }

    document.body.style.overflow = "";

    if (state.lastFocus && state.lastFocus.focus) {
      try {
        state.lastFocus.focus({ preventScroll: true });
      } catch (error) {
        /* ignore */
      }
    }
  }

  /* -------------------------------------------------------------- header/nav */

  function updateHeaderState(header) {
    if (!header) return;
    var overlay = header.getAttribute("data-overlay") === "true";
    var menuOpen = header.getAttribute("data-menu-open") === "true";
    var solid = !overlay || window.scrollY > 40 || menuOpen;

    ["bg-ink/95", "shadow-[0_1px_0_#c8a96a26]", "backdrop-blur"].forEach(function (name) {
      header.classList.toggle(name, solid);
    });
    header.classList.toggle("bg-transparent", !solid);
  }

  function updateAllHeaders() {
    document.querySelectorAll("[data-ac-header]").forEach(updateHeaderState);
  }

  function toggleMenu(button) {
    var header = button.closest("[data-ac-header]");
    if (!header) return;
    var menu = header.querySelector("[data-ac-menu]");
    if (!menu) return;

    var open = header.getAttribute("data-menu-open") !== "true";
    header.setAttribute("data-menu-open", String(open));
    menu.classList.toggle("hidden", !open);
    menu.classList.toggle("flex", open);
    button.setAttribute("aria-expanded", String(open));
    button.setAttribute("aria-label", open ? "Close menu" : "Open menu");

    var line1 = header.querySelector('[data-ac-burger="1"]');
    var line2 = header.querySelector('[data-ac-burger="2"]');
    var line3 = header.querySelector('[data-ac-burger="3"]');
    if (line1) {
      line1.classList.toggle("translate-y-[6.5px]", open);
      line1.classList.toggle("rotate-45", open);
    }
    if (line2) line2.classList.toggle("opacity-0", open);
    if (line3) {
      line3.classList.toggle("-translate-y-[6.5px]", open);
      line3.classList.toggle("-rotate-45", open);
    }

    updateHeaderState(header);
  }

  function closeMenu() {
    document.querySelectorAll('[data-ac-header][data-menu-open="true"]').forEach(function (header) {
      var button = header.querySelector("[data-ac-menu-toggle]");
      if (button) toggleMenu(button);
      else header.setAttribute("data-menu-open", "false");
    });
  }

  /* -------------------------------------------------------------- undo/remove */

  function showUndo(item) {
    var root = drawerRoot();
    if (!root) return;
    state.removed = item;
    if (state.cart) renderDrawer(state.cart);
    window.clearTimeout(state.undoTimeout);
    state.undoTimeout = window.setTimeout(function () {
      hideUndo();
    }, 6000);
  }

  function hideUndo() {
    if (!state.removed) return;
    state.removed = null;
    if (state.cart) renderDrawer(state.cart);
  }

  function removeLine(key) {
    var cart = state.cart;
    if (!cart) return;
    var item = (cart.items || []).filter(function (line) {
      return line.key === key;
    })[0];

    changeLine(key, 0)
      .then(function () {
        if (item) {
          showUndo({
            id: item.variant_id || item.id,
            quantity: item.quantity,
            title: item.product_title
          });
        }
        refreshCartPage();
      })
      .catch(reportError);
  }

  function undoRemove() {
    var removed = state.removed;
    if (!removed) return;
    state.removed = null;
    hideUndo();
    addToCart([{ id: removed.id, quantity: removed.quantity }], { open: false })
      .then(refreshCartPage)
      .catch(reportError);
  }

  /* --------------------------------------------------------------- cart page */

  function refreshCartPage() {
    var holder = document.querySelector("[data-ac-cart-page]");
    if (!holder) return Promise.resolve();

    var url = new URL(window.location.href);
    url.searchParams.set("section_id", "main-cart");

    return fetch(url.toString(), {
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" }
    })
      .then(function (response) {
        if (!response.ok) throw new Error("section fetch failed");
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var fresh = doc.querySelector("[data-ac-cart-page]");
        if (!fresh) throw new Error("missing section");
        holder.replaceWith(fresh);
        render();
      })
      .catch(function () {
        window.location.reload();
      });
  }

  function reportError(error) {
    if (window.console && console.error) console.error("[AC]", error);
    emit("ac:cart-error", error);
  }

  /* ------------------------------------------------------------------ events */

  function bindEvents() {
    document.addEventListener("click", function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var add = target.closest("[data-ac-add]");
      if (add) {
        event.preventDefault();
        var id = add.getAttribute("data-ac-add");
        var qty = Number(add.getAttribute("data-ac-qty") || 1);
        if (add.disabled) return;
        add.disabled = true;
        addToCart([{ id: id, quantity: qty }], { open: add.getAttribute("data-ac-open") !== "false" })
          .catch(reportError)
          .then(function () {
            add.disabled = false;
          });
        return;
      }

      if (target.closest("[data-ac-checkout]")) {
        window.location.href = route("checkout", "/checkout");
        return;
      }

      if (target.closest("[data-ac-cart-open]")) {
        event.preventDefault();
        openDrawer();
        return;
      }

      if (target.closest("[data-ac-menu] a")) {
        closeMenu();
      }

      var lineQty = target.closest("[data-ac-line-qty]");
      if (lineQty) {
        event.preventDefault();
        var key = lineQty.getAttribute("data-ac-line-qty");
        var delta = Number(lineQty.getAttribute("data-delta") || 0);
        var current = 0;
        var cart = state.cart;
        if (cart) {
          (cart.items || []).forEach(function (item) {
            if (item.key === key) current = item.quantity;
          });
        }
        var next = current + delta;
        if (next <= 0) {
          removeLine(key);
        } else {
          changeLine(key, next).then(refreshCartPage).catch(reportError);
        }
        return;
      }

      var lineRemove = target.closest("[data-ac-line-remove]");
      if (lineRemove) {
        event.preventDefault();
        removeLine(lineRemove.getAttribute("data-ac-line-remove"));
        return;
      }

      if (target.closest("[data-ac-undo]")) {
        event.preventDefault();
        undoRemove();
        return;
      }

      var pageQty = target.closest("[data-ac-page-qty]");
      if (pageQty) {
        event.preventDefault();
        var pageKey = pageQty.getAttribute("data-ac-page-qty");
        var pageDelta = Number(pageQty.getAttribute("data-delta") || 0);
        var row = pageQty.closest("[data-ac-cart-row]");
        var pageCurrent = row ? Number(row.getAttribute("data-qty") || 0) : 0;
        var pageNext = pageCurrent + pageDelta;
        if (pageNext <= 0) {
          changeLine(pageKey, 0).then(refreshCartPage).catch(reportError);
        } else {
          changeLine(pageKey, pageNext).then(refreshCartPage).catch(reportError);
        }
        return;
      }

      var pageRemove = target.closest("[data-ac-page-remove]");
      if (pageRemove) {
        event.preventDefault();
        changeLine(pageRemove.getAttribute("data-ac-page-remove"), 0)
          .then(refreshCartPage)
          .catch(reportError);
        return;
      }

      if (target.closest("[data-ac-menu-toggle]")) {
        event.preventDefault();
        toggleMenu(target.closest("[data-ac-menu-toggle]"));
        return;
      }

      if (target.closest("[data-ac-drawer-close]") || target.closest("[data-ac-drawer-x]") || target.closest("[data-ac-drawer-overlay]")) {
        closeDrawer();
        return;
      }
    });

    document.addEventListener("keydown", function (event) {
      if (event.key !== "Escape") return;
      if (state.drawerOpen) closeDrawer();
      closeMenu();
    });

    var scrollScheduled = false;
    window.addEventListener(
      "scroll",
      function () {
        if (scrollScheduled) return;
        scrollScheduled = true;
        window.requestAnimationFrame(function () {
          scrollScheduled = false;
          updateAllHeaders();
        });
      },
      { passive: true }
    );

    window.addEventListener("ac:cart-open", openDrawer);
    window.addEventListener("ac-cart-open", openDrawer);
    window.addEventListener("ac:cart-updated", function (event) {
      if (event.detail && !state.cart) state.cart = event.detail;
    });

    document.addEventListener("submit", function (event) {
      var form = event.target;
      if (form && form.id === "cart-form" && form.getAttribute("data-ac-enhanced") === "true") {
        event.preventDefault();
      }
    });
  }

  /* -------------------------------------------------------------------- init */

  function init() {
    updateAllHeaders();
    bindEvents();
    tick();
    window.setInterval(tick, 1000);

    refresh().catch(function (error) {
      if (window.console && console.warn) console.warn("[AC] cart unavailable", error);
    });
  }

  AC.addToCart = addToCart;
  AC.changeLine = changeLine;
  AC.updateCart = updateCart;
  AC.getCart = getCart;
  AC.refresh = refresh;
  AC.openDrawer = openDrawer;
  AC.closeDrawer = closeDrawer;
  AC.formatMoney = money;
  AC.money = money;
  AC.refreshCartPage = refreshCartPage;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
